/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import FingerprintsTable from './FingerprintsTable';
import {
  ColumnDef,
  RowSelectionState,
  SortingState,
  Updater,
} from '@tanstack/react-table'
import { StatusContext, StatusContextType } from './StatusContext';
import { Row, Col, Button } from 'react-bootstrap';
import { FingerprintsFilterControls, FingerprintFilterParams } from './FingerprintsFilterControls';
import { Stack, Badge } from 'react-bootstrap';
import { BeContext } from './BeContext';
import Dropdown from 'react-bootstrap/Dropdown';
import { getFingerprintsThinFiltered, FingerprintThin, beSelOpAll, beSelOpInvert, addToBeSelection, removeFromBeSelection, addSelectionFromFilter, removeSelectionFromFilter, createFingerprint } from '../data';
import { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

function FingerprintsPage() {
  const [fingerData, setFingerData] = useState<FingerprintThin[]>([]);
    const [fSelection, setFSelection] = useState<RowSelectionState>({});
    const statusContext = useContext(StatusContext);
    const [ searchParams, setSearchParams ] = useSearchParams();
    const beContext = useContext(BeContext);
    const theme : any = useTheme();
    const [ refreshFlag, setRefreshFlag ] = useState<boolean>(false);
    const tableScrollContainerRef = useRef<HTMLDivElement>(null);
    const [ scrollId, setScrollId ] = useState<number | undefined>(undefined);
    const [ scrollTrigger, setScrollTrigger ] = useState<number>(0);
    const navigate = useNavigate();
    const location = useLocation();
    const [ sorting, setSorting ] = useState<SortingState>([]);
    const [ pendingRowNavId, setPendingRowNavId ] = useState<number | undefined>(undefined);
    const [ idInputValue, setIdInputValue ] = useState<string>('');
    const [ allowViewFromIdSearch, setAllowViewFromIdSearch ] = useState<boolean>(false);
    const [ scrollBehavior, setScrollBehavior ] = useState<ScrollBehavior>('smooth');
    const [ tableResetKey, setTableResetKey ] = useState<number>(0);
    const lastViewSyncedFromScrollRef = useRef<string>('');
    const viewSyncDebounceTimerRef = useRef<number | undefined>(undefined);
    const lastAppliedViewParamRef = useRef<string>('');
    const didApplyInitialViewRef = useRef<boolean>(false);
    // Latest sort we own, plus the exact 'sort' param string it serializes to.
    //   Handlers and timers must never work from a render-captured copy of these
    //   or a slow render lets a stale value win and the sort snaps back.
    const sortingRef = useRef<SortingState>([]);
    const appliedSortParamRef = useRef<string>('');

    const selectedParam = searchParams.get('selected') || '';
    const sortParam = searchParams.get('sort') || '';
    const backendSelectedParam = searchParams.get('backendSelected') || '';
    const layerParam = searchParams.get('layer') || '';
    const tagParam = searchParams.get('tag') || '';
    const searchParam = searchParams.get('search') || '';

    let fParams : FingerprintFilterParams = { } as any;
    if (searchParams.has('selected')) {
      fParams.selected = searchParams.get('selected') || undefined;
    } else if (searchParams.has('backendSelected')) {
      // Backward-compat for old links before selected=inws/notinws migration.
      fParams.selected = searchParams.get('backendSelected') === 'true' ? 'inws' : 'notinws';
    }
    if (searchParams.has('layer')) {
      fParams.layer = parseInt(searchParams.get('layer') || '0');
    }
    if (searchParams.has('tag')) {
      fParams.tag = searchParams.get('tag') || '';
    }
    if (searchParams.has('search')) {
      fParams.search = searchParams.get('search') || '';
    }

    // Mutate the live URL rather than a render-captured snapshot of it. A write
    //   that runs later than the render it was created in (debounced scroll sync,
    //   a click handled while a big re-sort blocked the main thread) would
    //   otherwise put back every param as it looked when it was scheduled,
    //   silently undoing a newer sort.
    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;
    const updateSearchParams = useCallback((
      mutate: (params: URLSearchParams) => void,
      opts?: { replace?: boolean, preventScrollReset?: boolean }
    ) => {
      const next = new URLSearchParams(window.location.search);
      mutate(next);
      setSearchParamsRef.current(next, opts);
    }, []);

    useEffect(() => {
      async function doFetches(params : any) {
        try {
          const response = await getFingerprintsThinFiltered(params);
          setFingerData(response as FingerprintThin[]);
        } catch (error : any) {
          statusContext.postError(`Error fetching fingerprints/categories: ${error.message}`);
          console.error('Error fetching fingerprints/categories:', error);
        }
      }
      doFetches(fParams);
    }, [ selectedParam, backendSelectedParam, layerParam, tagParam, searchParam, refreshFlag ]);

    // Restore sort state from the URL, but only when the URL actually carries a
    //   sort we did not just write. Re-applying it on every param change (the
    //   view= sync fires one every 150ms while scrolling) handed the table a new
    //   sorting array each time, forcing a full re-sort of the whole dataset.
    useEffect(() => {
      if (sortParam === appliedSortParamRef.current) {
        return;
      }
      let restored : SortingState = [];
      if (sortParam) {
        try {
          restored = JSON.parse(sortParam);
        } catch (e) {
          // Invalid sort JSON: keep what we have rather than dropping the sort.
          restored = sortingRef.current;
        }
      }
      appliedSortParamRef.current = sortParam;
      sortingRef.current = restored;
      setSorting(restored);
    }, [ sortParam ]);

    // Restore the view parameter from URL
    useEffect(() => {
      const viewParam = searchParams.get('view') || '';
      const viewChanged = viewParam !== lastAppliedViewParamRef.current;
      const shouldApplyFromUrl = !didApplyInitialViewRef.current;

      if (viewParam) {
        const viewId = parseInt(viewParam || '0');
        if (viewParam && viewParam === lastViewSyncedFromScrollRef.current) {
          // This URL update came from scrolling; do not re-apply scroll.
        } else if ((allowViewFromIdSearch || shouldApplyFromUrl) && viewChanged && viewId && (idInputValue.trim() === '' || allowViewFromIdSearch)) {
          setScrollBehavior(allowViewFromIdSearch ? 'smooth' : 'auto');
          setScrollId(viewId);
          setScrollTrigger(prev => prev + 1);
          if (!allowViewFromIdSearch) {
            didApplyInitialViewRef.current = true;
            setTimeout(() => {
              setScrollId(undefined);
            }, 0);
          }
        }
      }

      lastAppliedViewParamRef.current = viewParam;

      if (allowViewFromIdSearch) {
        setAllowViewFromIdSearch(false);
      }
    }, [ searchParams, idInputValue, allowViewFromIdSearch ]);

    // Commit view into list URL before navigating away to avoid a history race.
    useEffect(() => {
      if (!pendingRowNavId) {
        return;
      }
      if ((searchParams.get('view') || '') !== String(pendingRowNavId)) {
        return;
      }

      navigate(`/fingerprint/${pendingRowNavId}`, {
        state: { returnUrl: getCurrentReturnUrl() },
      });
      setPendingRowNavId(undefined);
    }, [ pendingRowNavId, searchParams ]);

    function setFilter(newFilter: FingerprintFilterParams) {
      updateSearchParams((next) => {
        const setOrDelete = (key: keyof FingerprintFilterParams) => {
          const val = newFilter[key];
          if (val !== undefined && val !== null && val !== '') {
            next.set(String(key), String(val));
          } else {
            next.delete(String(key));
          }
        };

        setOrDelete('selected');
        setOrDelete('layer');
        setOrDelete('tag');
        setOrDelete('search');

        // Remove deprecated param so UI state cannot be overridden by stale URLs.
        next.delete('backendSelected');
      });
    }

    function handleIdSearch(id: number) {
      setAllowViewFromIdSearch(true);
      setScrollBehavior('smooth');
      updateSearchParams((next) => next.set('view', String(id)));
    }

    // Toggling off the render-captured `sorting` loses clicks that land while a
    //   re-sort is still rendering, so drive the toggle from the ref and advance
    //   it eagerly: back to back clicks then chain asc -> desc -> none correctly.
    const handleSortingChange = useCallback((updaterOrValue: Updater<SortingState>) => {
      const newSorting = typeof updaterOrValue === 'function'
        ? updaterOrValue(sortingRef.current)
        : updaterOrValue;
      // Filter to remove any undefined values before serializing
      const cleanSorting = newSorting.filter(s => s.id !== undefined).map(s => ({ id: s.id, ...(s.desc !== undefined && { desc: s.desc }) }));
      const sortStr = cleanSorting.length > 0 ? JSON.stringify(cleanSorting) : '';

      sortingRef.current = newSorting;
      appliedSortParamRef.current = sortStr;
      setSorting(newSorting);
      updateSearchParams((next) => {
        if (sortStr) {
          next.set('sort', sortStr);
        } else {
          next.delete('sort');
        }
      });
    }, [ updateSearchParams ]);

    const handleVisibleIdChange = useCallback((id: number) => {
      // Drop any armed write first: an unrelated early return used to leave one
      //   pending, and it would land after the next sort click.
      if (viewSyncDebounceTimerRef.current) {
        window.clearTimeout(viewSyncDebounceTimerRef.current);
        viewSyncDebounceTimerRef.current = undefined;
      }
      if (!id || (new URLSearchParams(window.location.search).get('view') || '') === String(id)) {
        return;
      }
      viewSyncDebounceTimerRef.current = window.setTimeout(() => {
        viewSyncDebounceTimerRef.current = undefined;
        lastViewSyncedFromScrollRef.current = String(id);
        updateSearchParams((next) => next.set('view', String(id)), { replace: true });
      }, 150);
    }, [ updateSearchParams ]);

    useEffect(() => {
      return () => {
        if (viewSyncDebounceTimerRef.current) {
          window.clearTimeout(viewSyncDebounceTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const forceResetTs = (location.state as any)?.forceResetTs;
      if (!forceResetTs) {
        return;
      }

      setFSelection({});
      setSorting([]);
      sortingRef.current = [];
      appliedSortParamRef.current = '';
      setScrollId(undefined);
      setScrollTrigger(0);
      setPendingRowNavId(undefined);
      setIdInputValue('');
      setAllowViewFromIdSearch(false);
      setScrollBehavior('smooth');
      lastViewSyncedFromScrollRef.current = '';
      lastAppliedViewParamRef.current = '';
      didApplyInitialViewRef.current = false;
      setTableResetKey(prev => prev + 1);
      if (tableScrollContainerRef.current) {
        tableScrollContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
      }

      navigate('/fingerprints', { replace: true, state: null });
    }, [location.state]);

    function handleRowClick(id: number) {
      const next = new URLSearchParams(window.location.search);
      next.set('view', String(id));
      setPendingRowNavId(id);
      navigate(
        {
          pathname: location.pathname,
          search: '?' + next.toString(),
        },
        {
          replace: true,
          preventScrollReset: true,
        }
      );
    }

    function getCurrentReturnUrl(): string {
      return location.pathname + location.search;
    }

    // In the top page we are in a Row that covers the main bottom part of the page,
    //    this is set to not scroll
    return (
      <Col>
        <Row>
          <Col xs={2} className="ms-2">
            <h3>Fingerprints</h3>
            <Stack direction="horizontal" gap={3}>
                <Badge pill bg="primary">All:{fingerData.length}</Badge>
                {fParams.layer && (
                    <Badge pill bg="success">
                        Layer: {fParams.layer}
                    </Badge>
                )}
                {fParams.tag && (
                    <Badge pill bg="success">
                        Tag: {fParams.tag}
                    </Badge>
                )}
                {fParams.selected && fParams.selected !== 'all' && (
                    <Badge pill bg="success">
                        Work
                    </Badge>
                )}
                <Badge pill bg="info">WS:{beContext.beSelection ? Object.keys(beContext.beSelection).length : 0}</Badge>
            </Stack>
            <div className="mt-3 d-flex align-items-center gap-2">
              <Dropdown onSelect={() => {}} id="workspace-actions">
                <Dropdown.Toggle size="sm" variant="success" >
                WorkingSet
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ backgroundColor: theme.colors.logBg, borderColor: '#198754' }}
                  >
                <Dropdown.Item
                  eventKey="sel2ws"
                  onClick={() => {
                    addToBeSelection(Object.keys(fSelection).map(k => parseInt(k)));
                    setRefreshFlag(!refreshFlag);
                  }}
                >
                  Add selection to working set
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="delws"
                  onClick={() => {
                    removeFromBeSelection(Object.keys(fSelection).map(k => parseInt(k)));
                    setRefreshFlag(!refreshFlag);
                  }}
                >
                  Remove selection from working set
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="clrws"
                  onClick={async () => {
                      await beSelOpAll(true);
                      setRefreshFlag(!refreshFlag);
                  }}
                >
                  Clear entire working set
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="invws"
                  onClick={async () => {
                      await beSelOpInvert();
                      setRefreshFlag(!refreshFlag);
                  }}
                >
                  Invert working set
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="addfws"
                  onClick={() => {
                    addSelectionFromFilter(fParams);
                    setRefreshFlag(!refreshFlag);
                  }}
                >
                  Add filtered (or all) to working set
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="delfws"
                  onClick={() => {
                    removeSelectionFromFilter(fParams);
                    setRefreshFlag(!refreshFlag);
                  }}
                >
                  Remove filtered from working set
                </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button
                size="sm"
                variant="outline-success"
                onClick={async () => {
                  try {
                    const id = await createFingerprint();
                    navigate(`/fingerprint/${id}`);
                  } catch (error : any) {
                    statusContext.postError(`Failed to create fingerprint: ${error.message}`);
                  }
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          </Col>
          <Col xs={8} className="me-2">
            <FingerprintsFilterControls filter={fParams} setFilter={setFilter} onIdSearch={handleIdSearch} onIdInputChange={setIdInputValue} />
          </Col>
        </Row>
        <Row className="mt-2 ms-1 me-2">
          <Col>
            <FingerprintsTable
              key={tableResetKey}
              data={fingerData}
              rowSelection={fSelection}
              setRowSelection={setFSelection}
              scrollContainerRef={tableScrollContainerRef}
              scrollToId={scrollId}
              scrollTrigger={scrollTrigger}
              scrollBehavior={scrollBehavior}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              onVisibleIdChange={handleVisibleIdChange}
              onRowClick={handleRowClick}
            />
          </Col>
        </Row>
      </Col>
    );
}

export default FingerprintsPage;